import { useTranslation } from 'next-i18next';
import { useCallback } from 'react';
import { Share } from '@capacitor/share';
import { toggleSnackbar } from '@bridebook/web/lib/bbcommon/actions';
import { getIsCordova } from '@bridebook/web/lib/mobile-app/selectors';
import { useDispatch, useSelector } from '@bridebook/web/lib/utils';
import { ShareModalProps } from './share-modal-context';
import { useShareModal } from './use-share-modal';

type ShareConfig = {
  /**
   * The URL to share - will be used for all sharing methods (native, modal, social)
   */
  url: string;

  /**
   * The title to share - will be used for Twitter/Facebook posts and as part of
   * the message in native share options. Also used as default modal title if
   * shareModalProps.title is not provided
   */
  shareTitle: string;

  /**
   * Optional text description to share - will be used for Twitter/Facebook posts
   * and as part of the message in native share options. Also used as default modal
   * subtitle if shareModalProps.subtitle is not provided
   */
  shareText?: string;

  /**
   * Optional props to customize the non-native share modal
   * - If title/subtitle are not provided, shareTitle/shareText will be used
   * - If onCopyLinkClick/onFacebookClick/onTwitterClick are not provided, default handlers will be used
   * - CustomComponent can be provided to add custom content to the modal
   * - Set title/subtitle to empty string/undefined to hide that section
   */
  shareModalProps?: ShareModalProps;

  /**
   * Forces the use of the modal share interface, ignoring native share capabilities.
   * Useful when you want to ensure the custom modal experience
   */
  overrideNative?: boolean;

  /**
   * Optional event callbacks for share-related user interactions.
   * Called with the shared `url` before the associated action runs.
   *
   * Events:
   * - `onClick`: Non-native share initiation
   * - `onCopy`: User clicks to copy link
   * - `onFacebook`: Facebook share click
   * - `onTwitter`: Twitter share click
   * - `onCordova`: Native share via Cordova
   * - `onWeb`: Native share via Web Share API
   */
  shareEvents?: Partial<{
    onClick: (url: string) => void;
    onCopy: (url: string) => void;
    onFacebook: (url: string) => void;
    onTwitter: (url: string) => void;
    onCordova: (url: string) => void;
    onWeb: (url: string) => void;
  }>;
};

export const useShareLink = ({
  url,
  shareTitle,
  shareText,
  shareModalProps,
  overrideNative,
  shareEvents: { onClick, onCopy, onFacebook, onTwitter, onCordova, onWeb } = {},
}: ShareConfig) => {
  const { t } = useTranslation('common');
  const isCordovaApp = useSelector(getIsCordova);
  const { openModal, closeModal } = useShareModal();
  const dispatch = useDispatch();

  const shareLink = useCallback(async () => {
    const isNativeShareAvailable = !overrideNative && !!(isCordovaApp || navigator?.share);

    try {
      if (!isNativeShareAvailable) {
        onClick?.(url);
        openModal({
          title: shareModalProps?.title ?? shareTitle,
          subtitle: shareModalProps?.subtitle ?? shareText,
          onCopyLinkClick:
            shareModalProps?.onCopyLinkClick ??
            (() => {
              onCopy?.(url);
              navigator.clipboard.writeText(url);
              dispatch(
                toggleSnackbar('success', t('share.linkCopiedToClipboard'), 3000, {
                  icon: '🎉',
                }),
              );
              closeModal();
            }),
          onFacebookClick:
            shareModalProps?.onFacebookClick ??
            (() => {
              onFacebook?.(url);
              window.open(
                `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                  url,
                )}&quote=${encodeURIComponent(shareTitle)}`,
                '_blank',
              );
              closeModal();
            }),
          onTwitterClick:
            shareModalProps?.onTwitterClick ??
            (() => {
              onTwitter?.(url);
              window.open(
                `https://x.com/intent/tweet?url=${encodeURIComponent(
                  url,
                )}&text=${encodeURIComponent(shareTitle)}`,
                '_blank',
              );
              closeModal();
            }),
        });
        return;
      }

      if (isCordovaApp) {
        onCordova?.(url);
        await Share.share({
          // `text` has priority being copied over `url` if it is passed
          url,
          title: shareTitle,
          dialogTitle: shareTitle,
        });
        return;
      }

      onWeb?.(url);
      await navigator.share({
        // `text` has priority being copied over `url` if it is passed
        url,
        title: shareTitle,
      });
    } catch (e) {
      // Share was cancelled
    }
  }, [
    overrideNative,
    isCordovaApp,
    onWeb,
    url,
    shareTitle,
    shareText,
    onClick,
    openModal,
    shareModalProps?.title,
    shareModalProps?.subtitle,
    shareModalProps?.onCopyLinkClick,
    shareModalProps?.onFacebookClick,
    shareModalProps?.onTwitterClick,
    onCopy,
    dispatch,
    t,
    closeModal,
    onFacebook,
    onTwitter,
    onCordova,
  ]);

  return {
    shareLink,
    isNativeShareAvailable: !!(isCordovaApp || navigator?.share),
  };
};
